// Favorite teams — localStorage-persisted set of {sport, team} pairs shared
// across the React tree via a Context so toggling in `FavoriteButton` is
// immediately visible to `Tracker`'s filter row.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "tracker.favorites.v1";

function readFavs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((f) => f && f.sport && f.team);
  } catch { return []; }
}

export function favKey(sport, team) {
  return `${(sport || "").toLowerCase()}|${(team || "").toLowerCase()}`;
}

const Ctx = createContext(null);

export function FavoritesProvider({ children }) {
  const [favorites, setFavorites] = useState(readFavs);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites)); } catch {}
  }, [favorites]);

  const toggle = useCallback((sport, team) => {
    if (!sport || !team) return;
    const k = favKey(sport, team);
    setFavorites((prev) => {
      const has = prev.some((f) => favKey(f.sport, f.team) === k);
      if (has) return prev.filter((f) => favKey(f.sport, f.team) !== k);
      return [...prev, { sport, team }];
    });
  }, []);

  const isFavorite = useCallback((sport, team) => {
    const k = favKey(sport, team);
    return favorites.some((f) => favKey(f.sport, f.team) === k);
  }, [favorites]);

  const clear = useCallback(() => setFavorites([]), []);

  const gameMatchesFavorites = useCallback((game) => {
    if (favorites.length === 0) return false;
    const sport = game?.sport;
    if (!sport) return false;
    return (
      isFavorite(sport, game.home_team) ||
      isFavorite(sport, game.away_team)
    );
  }, [favorites, isFavorite]);

  const value = useMemo(() => ({
    favorites,
    toggle,
    isFavorite,
    clear,
    hasAny: favorites.length > 0,
    count: favorites.length,
    gameMatchesFavorites,
  }), [favorites, toggle, isFavorite, clear, gameMatchesFavorites]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useFavorites() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useFavorites must be used inside <FavoritesProvider>");
  return v;
}
